import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { Trash2, Pencil, Plus } from "lucide-react";

const EMPTY = {
  title_fr: "", title_pt: "", description_fr: "", description_pt: "",
  event_date: "", meet_link: "", spots_available: 10, published: true,
};

const toLocalInput = (iso) => {
  try {
    return new Date(iso).toISOString().slice(0, 16);
  } catch {
    return "";
  }
};

export const EventsManager = () => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data: events = [] } = useQuery({ queryKey: ["admin-events"], queryFn: async () => (await api.get("/admin/events")).data });

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...form, event_date: new Date(form.event_date).toISOString(), spots_available: Number(form.spots_available) };
      return editing ? api.put(`/admin/events/${editing.id}`, payload) : api.post("/admin/events", payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-events"] });
      qc.invalidateQueries({ queryKey: ["events"] });
      toast("Evento salvo!");
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
    },
    onError: (e) => toast(formatApiError(e.response?.data?.detail)),
  });

  const remove = useMutation({
    mutationFn: async (id) => api.delete(`/admin/events/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-events"] });
      qc.invalidateQueries({ queryKey: ["events"] });
      toast("Evento removido.");
    },
  });

  const openEdit = (ev) => { setEditing(ev); setForm({ ...ev, event_date: toLocalInput(ev.event_date) }); setOpen(true); };
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };

  return (
    <div className="mt-6" data-testid="events-manager">
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} data-testid="event-new-button"><Plus className="w-4 h-4 mr-1" /> Novo evento</Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow><TableHead>Título (PT)</TableHead><TableHead>Data</TableHead><TableHead>Vagas</TableHead><TableHead /></TableRow>
        </TableHeader>
        <TableBody>
          {events.map((ev) => (
            <TableRow key={ev.id} data-testid={`event-row-${ev.id}`}>
              <TableCell>{ev.title_pt}</TableCell>
              <TableCell>{new Date(ev.event_date).toLocaleString("pt-BR")}</TableCell>
              <TableCell>{ev.spots_available}</TableCell>
              <TableCell className="flex gap-2 justify-end">
                <Button size="icon" variant="ghost" onClick={() => openEdit(ev)} data-testid={`event-edit-${ev.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove.mutate(ev.id)} data-testid={`event-delete-${ev.id}`}><Trash2 className="w-4 h-4" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="event-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Editar evento" : "Novo evento"}</DialogTitle><DialogDescription>Defina data, link do Meet e vagas disponíveis.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Título (PT)</Label><Input data-testid="event-input-title_pt" value={form.title_pt} onChange={(e) => setForm({ ...form, title_pt: e.target.value })} /></div>
            <div><Label>Título (FR)</Label><Input value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} /></div>
            <div><Label>Descrição (PT)</Label><Textarea value={form.description_pt} onChange={(e) => setForm({ ...form, description_pt: e.target.value })} /></div>
            <div><Label>Descrição (FR)</Label><Textarea value={form.description_fr} onChange={(e) => setForm({ ...form, description_fr: e.target.value })} /></div>
            <div><Label>Data / hora</Label><Input type="datetime-local" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} /></div>
            <div><Label>Link do Meet</Label><Input value={form.meet_link} onChange={(e) => setForm({ ...form, meet_link: e.target.value })} /></div>
            <div><Label>Vagas disponíveis</Label><Input type="number" value={form.spots_available} onChange={(e) => setForm({ ...form, spots_available: e.target.value })} /></div>
          </div>
          <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="event-save-button" className="mt-4">Salvar</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
